/**
 * =============================================================================
 * brandConfig.ts - Configurazione White Label del Portale
 * =============================================================================
 *
 * File centralizzato per tutte le informazioni di brand.
 * Per personalizzare il portale per un'altra organizzazione, modificare
 * SOLO questo file. Nessun altro file dovrebbe contenere riferimenti
 * hard-coded al brand.
 *
 * ISTRUZIONI PER LA PERSONALIZZAZIONE:
 * 1. Cambiare organizationName e shortName
 * 2. Sostituire logoUrl con il path del nuovo logo
 * 3. Aggiornare i colori in src/index.css (variabili CSS --primary, etc.)
 * 4. Modificare i link social e contatti
 *
 * @author CCFN Development Team
 * @version 1.0.0
 * =============================================================================
 */

import logoCcfn from "@/assets/logo-ccfn.png";

/**
 * Configurazione centralizzata del brand
 * Tutti i componenti UI devono riferirsi a questo oggetto
 */
export const brandConfig = {
  // ===== IDENTITÀ ORGANIZZAZIONE =====
  /** Nome completo dell'organizzazione */
  organizationName: "Lega Navale Italiana",

  /** Nome abbreviato per UI compatte (navbar, footer, etc.) */
  shortName: "LNI",

  /** Nome del centro/dipartimento formativo */
  departmentName: "Centro Culturale per la Formazione Nautica",

  /** Abbreviazione del dipartimento */
  departmentShortName: "CCFN",

  /** Tagline/slogan breve */
  tagline: "La formazione degli istruttori nautici e i percorsi didattici",

  // ===== ASSETS GRAFICI =====
  /** Path del logo principale (importato come modulo ES6) */
  logoUrl: logoCcfn,

  /** Testo alternativo per il logo - generato dinamicamente */
  logoAlt: "Logo Centro Formazione Nautica",

  // ===== WEBMASTER =====
  /**
   * Informazioni sul webmaster/sviluppatore.
   * Se null, la sezione webmaster non viene mostrata nel footer.
   */
  /** COMMENTATO MANUALENTE PER OMETTERE IL WEBMASTER DAL FOOTER
   * PER RIPRISTINARE, DECOMMENTARE QUESTE RIGHE E MODIFICARE I VALORI
   * E COMMENTARE LA RIGA webmaster: null, QUI SOTTO
  webmaster: {
    name: "Luciano Marcelli",
    url: "https://luciano-marcelli.it",
    email: "luciano.marcelli.lm@gmail.com",
  } as { name: string; url: string; email: string } | null,
  */
  // IMPOSTATO A NULL PER OMETTERE IL WEBMASTER DAL FOOTER
  webmaster: null,

  // ===== COLORI BRAND =====
  /**
   * Colori di riferimento - I colori effettivi sono definiti in index.css
   * Questi valori sono solo per riferimento e documentazione
   */
  colors: {
    /** Blu primario LNI - HSL: 205 85% 35% */
    primary: "#0D66A5",
    /** Colore secondario (sabbia) */
    secondary: "#f5e6d3",
    /** Colore accento (corallo) */
    accent: "#e85d3b",
  },

  // ===== CONTATTI =====
  /** Email di supporto */
  supportEmail: "formazione@leganavale.it",

  /** Sito web ufficiale dell'organizzazione */
  websiteUrl: "https://www.leganavale.it",

  /** Link al portale formazione */
  portalUrl: "https://lni-formazione.it",

  // ===== SOCIAL =====
  social: {
    facebook: "https://www.facebook.com/LeganavaleItaliana",
    instagram: "https://www.instagram.com/leganavaleitaliana/",
    youtube: "https://www.youtube.com/@leganavaleitaliana",
  },

  /** Anno di fondazione per copyright dinamico */
  foundingYear: 1897,

  /** Testo copyright - usa {year} come placeholder per l'anno corrente */
  copyrightText: "{shortName} Formazione. Tutti i diritti riservati.",

  // ===== DATI PAGAMENTO ISCRIZIONI =====
  /**
   * Configurazione pagamento per le domande di iscrizione.
   * Modificare questi valori con i dati reali dell'organizzazione.
   */
  payment: {
    /** IBAN per bonifico */
    iban: "IT00 X 00000 00000 000000000000",
    /** Intestatario del conto - usa {organizationName} per inserimento dinamico */
    beneficiary: "{organizationName}",
    /** Banca */
    bankName: "Banca d'Italia",
    /** Causale suggerita (usa {name}, {year} come placeholder) */
    defaultCausale: "Quota associativa {year} - {name}",
    /** Importo quota base (opzionale, può variare per sezione) */
    baseAmount: "€ 50,00",
    /** Note aggiuntive sul pagamento */
    paymentNotes: "La quota può variare in base alla tipologia di socio e alla sezione di appartenenza. Contattare la segreteria per informazioni.",
  },

  // ===== DATI LEGALI (GDPR/Privacy) =====
  /**
   * Informazioni legali per conformità GDPR.
   * Usati nelle pagine Privacy Policy, Cookie Policy, Termini di Servizio.
   * Se un campo è null/undefined, la relativa sezione non viene mostrata.
   */
  legal: {
    /** Nome formale del Titolare del Trattamento */
    officialName: "Presidenza Nazionale della Lega Navale Italiana",
    /** Indirizzo sede legale del Titolare */
    headquarters: "Via Guidubaldo Del Monte, 54 - 00197 Roma",
    /** Codice Fiscale o Partita IVA */
    vatNumber: "80126750586",
    /** Email ufficiale per questioni privacy */
    privacyEmail: "direttore@leganavale.it",
    /**
     * Dettagli del DPO (Data Protection Officer).
     * Se presente, viene mostrato nelle pagine legali.
     * Lasciare null/undefined se l'organizzazione non ha un DPO nominato.
     */
    dpoDetails: "Studio Legale D'Angelo - Via G. Antonelli, 47 - Roma",
    /** Data di nomina del DPO (opzionale, per riferimento) */
    dpoAppointmentDate: "8 novembre 2018",
    /** Numero protocollo nomina DPO (opzionale) */
    dpoAppointmentRef: "Lettera n. 1426",
  },

  // ===== METADATI SEO =====
  seo: {
    defaultTitle: "LNI Formazione - Centro Culturale per la Formazione Nautica",
    defaultDescription:
      "Piattaforma formativa della Lega Navale Italiana per la formazione istruttori e i percorsi didattici per soci.",
  },
} as const;

/**
 * Helper per ottenere il copyright con l'anno corrente
 */
export function getCopyrightText(): string {
  return `© ${new Date().getFullYear()} ${brandConfig.copyrightText.replace("{shortName}", brandConfig.shortName)}`;
}

/**
 * Helper per ottenere il nome completo con dipartimento
 */
export function getFullBrandName(): string {
  return `${brandConfig.shortName} ${brandConfig.departmentShortName}`;
}

export default brandConfig;
